import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Flex,
  SelectField,
  SwitchField,
  TextAreaField,
  TextField,
  Text,
  Label,
} from "@aws-amplify/ui-react";
import { TagsInput } from "react-tag-input-component";
import { useNavigate } from "react-router-dom";
import { useImmunizationsPostMutation } from "./api/useImmunizationsMutation";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
import { useState } from "react";
const { z } = require("zod");

const postImmunizationsRequest = z.object({
  immunizationName: z
    .string({ message: "Name is Required" })
    .min(1, { message: "Name is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  immunizationDescription: z.string({ message: "Description is Required" }),
  administeredDates: z.array(z.string().date()).nullish(),
  administeredLocation: z.string({
    message: "Administered Location is Required",
  }),
  dosage: z
    .string({ message: "Dosage is Required" })
    .min(1, { message: "Dosage is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  method: z.string({ message: "Method is Required" }),
  detailsTags: z.optional(z.array(z.string())),
  isActive: z.number().int(),
});

const requiredValidations = postImmunizationsRequest.required();

export default function ImmunizationsAddPage() {
  const [administeredDates, setAdministeredDates] = useState([]);
  const { data: userQueryData } = useUserQuery();
  const { mutate: immunizationsPostMutation } = useImmunizationsPostMutation();
  const {
    control,
    handleSubmit,
    reset,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      immunizationName: "",
      immunizationDescription: "",
      administeredDates: [],
      administeredLocation: "",
      dosage: "",
      method: "",
      detailsTags: [],
      isActive: 0,
    },
    resolver: zodResolver(requiredValidations),
  });

  const navigate = useNavigate();

  const administeredDatesAdd = (administeredDate) => {
    const administeredDateFormatted = formatDate(new Date(administeredDate));
    if (!administeredDates.some((ad) => ad === administeredDateFormatted)) {
      setAdministeredDates((pre) => {
        const newValue = [...pre, administeredDateFormatted];
        setValue("administeredDates", newValue);
        return newValue;
      });
    }
    setValue("administeredDate", null);
  };

  const administeredDatesRemove = (index) => {
    let cloneArray = Object.assign([], administeredDates);
    cloneArray.splice(index, 1);
    setAdministeredDates(() => {
      setValue("administeredDates", cloneArray);
      return cloneArray;
    });
  };

  const onSubmit = (values) => {
    immunizationsPostMutation({
      patientUserId: userQueryData?.patientUserId,
      ...values,
    });
    reset();
    navigate("/user/hr/immunizations");
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexFormContainer headerTitle={"Immunization Details"}>
          <Flex
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Controller
              name="immunizationName"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Immunization Name"
                  {...field}
                  errorMessage={errors.immunizationName?.message}
                  hasError={!!errors.immunizationName}
                />
              )}
            />
            <Controller
              name="immunizationDescription"
              control={control}
              render={({ field }) => (
                <TextAreaField
                  minWidth="300px"
                  label="Immunization Description"
                  {...field}
                  errorMessage={errors.immunizationDescription?.message}
                  hasError={!!errors.immunizationDescription}
                />
              )}
            />
            <Label htmlFor="administeredDate">Administered Dates</Label>

            <Controller
              name="administeredDate"
              control={control}
              render={({ field }) => (
                <Flex>
                  <DatePicker
                    onChange={(value) => field.onChange(value)}
                    value={field.value || null}
                    format="yyyy-MM-dd"
                    oneTap
                    style={{ width: "100%" }}
                  />
                  <Button
                    size="small"
                    disabled={!field.value}
                    onClick={() => administeredDatesAdd(field.value)}
                  >
                    Add
                  </Button>
                </Flex>
              )}
            />

            <Flex gap={9} direction={"column"}>
              {administeredDates?.map((ad, i) => {
                return (
                  <Flex
                    key={`iap_ad_${ad}_${i}`}
                    alignItems={"center"}
                    backgroundColor={"rgb(233, 249, 252)"}
                    paddingLeft={15}
                    paddingRight={15}
                    paddingTop={9}
                    paddingBottom={9}
                    borderRadius={12}
                  >
                    <Text>{ad}</Text>
                    <Button
                      size="small"
                      colorTheme="overlay"
                      onClick={() => administeredDatesRemove(i)}
                    >
                      X
                    </Button>
                  </Flex>
                );
              })}
            </Flex>

            <Controller
              name="administeredLocation"
              control={control}
              render={({ field }) => (
                <SelectField
                  minWidth="300px"
                  label="Administered Location"
                  placeholder="Select"
                  {...field}
                  errorMessage={errors.administeredLocation?.message}
                  hasError={!!errors.administeredLocation}
                >
                  <option value="Left Arm">Left Arm</option>
                  <option value="Right Arm">Right Arm</option>
                  <option value="Left Thigh">Left Thigh</option>
                  <option value="Right Thigh">Right Thigh</option>
                  <option value="Left Buttcheek">Left Buttcheek</option>
                  <option value="Right Buttcheek">Right Buttcheek</option>
                </SelectField>
              )}
            />
            <Controller
              name="dosage"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Dosage"
                  {...field}
                  errorMessage={errors.dosage?.message}
                  hasError={!!errors.dosage}
                />
              )}
            />
            <Controller
              name="method"
              control={control}
              render={({ field }) => (
                <SelectField
                  minWidth="300px"
                  label="Method"
                  placeholder="Select"
                  {...field}
                  errorMessage={errors.method?.message}
                  hasError={!!errors.method}
                >
                  <option value="Injected">Injected</option>
                  <option value="Oral">Oral</option>
                </SelectField>
              )}
            />
            <Controller
              name="detailsTags"
              control={control}
              render={({ field }) => {
                let value = field.value ?? [];
                if (typeof value === "string") {
                  try {
                    value = JSON.parse(value);
                  } catch (error) {
                    console.error("Failed to parse detailsTags:", error);
                    value = [];
                  }
                }
                if (!Array.isArray(value)) {
                  value = [];
                }
                return (
                  <TagsInput
                    name={field.name}
                    control={field.control}
                    placeHolder="Tags"
                    onChange={(tags) => field.onChange(tags)}
                    value={value}
                  />
                );
              }}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="Is Active"
                  labelPosition="end"
                  {...field}
                  isChecked={field.value === 1}
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Button variation="primary" type="submit">
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    </>
  );
}
